import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { format, addDays, isBefore } from 'date-fns';
import { useData } from '../contexts/DataContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import SectionHeader from '../components/SectionHeader';

const { FiPlus, FiCalendar, FiCheck, FiClock, FiTrash2, FiAlertCircle } = FiIcons;

const Timeline = () => {
  const { data, addTimelineTask, updateTimelineTask, deleteEntry } = useData();
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    task: '',
    dueDate: format(addDays(new Date(), 1), 'yyyy-MM-dd'),
    priority: 'medium',
    category: 'maintenance',
    notes: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    addTimelineTask({
      ...formData,
      status: 'pending'
    });
    setFormData({
      task: '',
      dueDate: format(addDays(new Date(), 1), 'yyyy-MM-dd'),
      priority: 'medium',
      category: 'maintenance',
      notes: ''
    });
    setShowForm(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const markTaskComplete = (taskId) => {
    updateTimelineTask(taskId, { status: 'completed' });
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-red-600 bg-red-100';
      case 'medium': return 'text-orange-600 bg-orange-100';
      case 'low': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'maintenance': return FiClock;
      case 'feeding': return FiCalendar;
      case 'water': return FiCheck;
      default: return FiAlertCircle;
    }
  };

  return (
    <div className="p-4 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Timeline</h1>
          <p className="text-gray-600 mt-1">Schedule and track farm tasks</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center space-x-2 bg-amber-500 text-white px-4 py-2 rounded-lg hover:bg-amber-600 transition-colors"
        >
          <SafeIcon icon={FiPlus} className="w-4 h-4" />
          <span>Add Task</span>
        </button>
      </div>

      {/* Task Management Tips */}
      <div className="bg-amber-50 p-4 rounded-lg">
        <h3 className="text-lg font-semibold text-amber-800 mb-2">Task Management Tips</h3>
        <div className="space-y-2 text-amber-700">
          <p className="flex items-center">
            <SafeIcon icon={FiCalendar} className="w-4 h-4 mr-2" />
            Schedule routine tasks on a regular basis
          </p>
          <p className="flex items-center">
            <SafeIcon icon={FiAlertCircle} className="w-4 h-4 mr-2" />
            Prioritize water quality checks
          </p>
          <p className="flex items-center">
            <SafeIcon icon={FiClock} className="w-4 h-4 mr-2" />
            Set reminders for feeding schedule changes
          </p>
          <p className="flex items-center">
            <SafeIcon icon={FiCheck} className="w-4 h-4 mr-2" />
            Complete high-priority tasks first
          </p>
        </div>
      </div>

      {/* Add Task Form */}
      {showForm && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
        >
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Add New Task</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Task Description
              </label>
              <input
                type="text"
                name="task"
                value={formData.task}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Due Date
                </label>
                <input
                  type="date"
                  name="dueDate"
                  value={formData.dueDate}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Priority
                </label>
                <select
                  name="priority"
                  value={formData.priority}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              >
                <option value="maintenance">Maintenance</option>
                <option value="feeding">Feeding</option>
                <option value="water">Water Quality</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                placeholder="Additional details or instructions..."
              />
            </div>
            <div className="flex space-x-3">
              <button
                type="submit"
                className="flex-1 bg-amber-500 text-white py-2 px-4 rounded-lg hover:bg-amber-600 transition-colors"
              >
                Add Task
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="flex-1 bg-gray-500 text-white py-2 px-4 rounded-lg hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Task Lists */}
      <div className="space-y-6">
        {/* Pending Tasks */}
        <div>
          <SectionHeader 
            title="Pending Tasks" 
            infoText="Tasks that need to be completed. Prioritize overdue and high-priority tasks."
          />
          <div className="space-y-3">
            {data.timeline
              .filter(task => task.status === 'pending')
              .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))
              .map(task => {
                const isOverdue = isBefore(new Date(task.dueDate), new Date());
                const CategoryIcon = getCategoryIcon(task.category);
                return (
                  <motion.div
                    key={task.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`bg-white rounded-xl p-4 shadow-sm border ${
                      isOverdue ? 'border-red-200' : 'border-gray-200'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                          <SafeIcon icon={CategoryIcon} className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">{task.task}</h3>
                          <p className="text-sm text-gray-500">
                            Due: {format(new Date(task.dueDate), 'MMM dd, yyyy')}
                            {isOverdue && (
                              <span className="text-red-600 ml-2">(Overdue)</span>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <span className={`text-xs px-2 py-1 rounded ${getPriorityColor(task.priority)}`}>
                          {task.priority}
                        </span>
                      </div>
                    </div>
                    {task.notes && (
                      <p className="text-sm text-gray-600 bg-gray-50 rounded p-2 mb-3">
                        {task.notes}
                      </p>
                    )}
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() => markTaskComplete(task.id)}
                        className="px-3 py-1 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                      >
                        <SafeIcon icon={FiCheck} className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deleteEntry('timeline', task.id)}
                        className="px-3 py-1 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                      </button>
                    </div>
                  </motion.div>
                );
              })}
          </div>
        </div>

        {/* Completed Tasks */}
        <div>
          <SectionHeader 
            title="Completed Tasks" 
            infoText="Recently completed tasks. These serve as a record of completed work."
          />
          <div className="space-y-3">
            {data.timeline
              .filter(task => task.status === 'completed')
              .slice(0, 5) // Show only last 5 completed tasks
              .map(task => {
                const CategoryIcon = getCategoryIcon(task.category);
                return (
                  <motion.div
                    key={task.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-gray-50 rounded-xl p-4 shadow-sm border border-gray-200"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center">
                          <SafeIcon icon={CategoryIcon} className="w-5 h-5 text-gray-600" />
                        </div>
                        <div>
                          <h3 className="font-medium text-gray-600 line-through">
                            {task.task}
                          </h3>
                          <p className="text-sm text-gray-500">
                            Completed: {format(new Date(task.completedAt || task.dueDate), 'MMM dd, yyyy')}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => deleteEntry('timeline', task.id)}
                        className="px-3 py-1 text-gray-500 hover:bg-gray-200 rounded-lg transition-colors"
                      >
                        <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                      </button>
                    </div>
                  </motion.div>
                );
              })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Timeline;